/* eslint-disable @typescript-eslint/no-explicit-any */

import * as React from 'react';

import { IOfferSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import {
  IGetInfrastructureForMapRequest,
  EDealType,
  EOfferType,
} from '../../../repositories/infrastructure-caching/v1/get-infrastructure-for-map';
import { ECategory } from '../../../repositories/infrastructure-caching/entities/infrastructure/CategoryItemsSchema';

import { IInfrastructureItem } from './types';

import { InfrastructureTabs } from './tabs';
import { InfrastructurePlacemarks } from './placemarks';
import { ActiveItemPopup } from './active-item-popup';
import { trackInfrastructureOpen, trackInfrastructureSelectCategory, trackInfrastructurePinClick } from './tracking';

import * as styles from './styles.css';

export interface IInfrastructureMapExtensionProps {
  map: YMaps.Map;
  objectManager: YMaps.ObjectManager;
  offer: IOfferSchema;

  bbox?: string | null;
  items: IInfrastructureItem[];

  // tslint:disable-next-line: no-any
  fetchInfrastructureMap(params: IGetInfrastructureForMapRequest): Promise<any>;
}

export interface IInfrastructureMapExtensionState {
  items: IInfrastructureItem[];
  visibleItems: IInfrastructureItem[];
  activeItem: IInfrastructureItem | null;
  activeItemPinned?: boolean;
  categories: ECategory[];
}

// tslint:disable-next-line: max-line-length
export class InfrastructureMapExtension extends React.Component<
  IInfrastructureMapExtensionProps,
  IInfrastructureMapExtensionState
> {
  public state: IInfrastructureMapExtensionState = {
    items: [],
    visibleItems: [],
    categories: [],
    activeItem: null,
  };

  private setBoundsTimeout: number;

  public componentDidMount() {
    const { bbox } = this.props;

    if (bbox) {
      this.setBoundsTimeout = window.setTimeout(() => {
        this.setBounds(bbox);
      }, 0);
    } else {
      this.ensureInfrastructure();
    }

    trackInfrastructureOpen();
  }

  public componentDidUpdate(prevProps: IInfrastructureMapExtensionProps) {
    const { bbox } = this.props;

    if (bbox && !prevProps.bbox) {
      this.setBounds(bbox);
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('load', this.fetchInfrastructure);

    clearTimeout(this.setBoundsTimeout);
  }

  public render() {
    const { categories } = this.state;

    return (
      <>
        <InfrastructurePlacemarks
          map={this.props.map}
          items={this.state.visibleItems}
          onActive={this.handleSetActiveItem}
          activeItem={this.state.activeItem}
          activeItemPinned={this.state.activeItemPinned}
        />
        <InfrastructureTabs
          className={styles['tabs']}
          selected={categories}
          onSelect={this.handleInfrastructureSelect}
        />
        <ActiveItemPopup
          item={this.state.activeItem}
          setItem={this.handleSetActiveItem}
          onClose={this.handleCloseActiveItemPopup}
          pinned={this.state.activeItemPinned}
        />
      </>
    );
  }

  private handleSetActiveItem = (activeItem: IInfrastructureItem | null, pinned?: boolean) => {
    if (this.state.activeItemPinned && pinned === undefined) {
      return;
    }

    this.setState({
      activeItem,
      activeItemPinned: pinned,
    });

    if (pinned) {
      trackInfrastructurePinClick();
    }
  };

  private handleCloseActiveItemPopup = () => {
    this.setState({
      activeItem: null,
      activeItemPinned: false,
    });
  };

  private handleInfrastructureSelect = (type: ECategory) => {
    const shouldSelect = !this.state.categories.includes(type);

    if (shouldSelect) {
      trackInfrastructureSelectCategory(type);

      this.setCategories(this.state.categories.concat(type));
    } else {
      this.setCategories(this.state.categories.filter(t => t !== type));
    }
  };

  private setCategories = (categories: ECategory[]) => {
    this.setState({
      categories,
      visibleItems: this.props.items.filter(({ category }) => categories.includes(category)),
    });
  };

  private setBounds(bbox: string) {
    const coords = bbox.split(',').map(Number);

    this.props.map.setBounds([
      [coords[0], coords[1]],
      [coords[2], coords[3]],
    ]);
  }

  private ensureInfrastructure = () => {
    // Нужно дождаться загрузки CSS
    if (document.readyState === 'complete') {
      this.fetchInfrastructure();
    } else {
      window.addEventListener('load', this.fetchInfrastructure);
    }
  };

  private fetchInfrastructure = () => {
    const size = this.props.map.container.getSize();

    this.props.fetchInfrastructureMap({
      aspectRatio: size.join(':'),
      dealType: this.props.offer.dealType as EDealType,
      offerType: this.props.offer.offerType as EOfferType,
      realtyOfferId: this.props.offer.id,
    });
  };
}
